import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin, Lock, Unlock, Loader2, Compass, Gift, CheckCircle2, ArrowLeft,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function WaymarkDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/waymarks/${id}`);
      setW(data.waymark);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Waymark not found");
      nav("/waymarks");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const claim = () => {
    if (!user) { nav("/login"); return; }
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setClaiming(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.post(`/waymarks/${id}/claim`, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
          toast.success(data.first_time ? "Waymark unlocked — nice find." : "You're all checked in.");
          setW((prev) => ({ ...prev, ...data.waymark, _unlocked: true }));
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Couldn't unlock");
        } finally {
          setClaiming(false);
        }
      },
      (err) => {
        setClaiming(false);
        toast.error(err.message || "Location permission required");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  if (loading) return <div className="py-20 text-center text-[#F5F2E8]/60">Loading…</div>;
  if (!w) return null;

  const unlocked = !!w.fun_fact; // backend returns fun_fact only after a valid claim

  return (
    <div className="py-8 space-y-6">
      <Link to="/waymarks" className="btn-ghost text-xs inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Back to waymarks
      </Link>

      <div
        className="loot-card-flat p-8 space-y-3"
        style={w.is_sponsored ? {
          borderColor: "#D7A62A",
          background: "linear-gradient(135deg, rgba(215,166,42,0.08), rgba(15,15,15,1) 65%)",
        } : undefined}
      >
        <div className="flex items-center gap-2 text-[#76B82A]">
          <span className="overline">{w.category || "waymark"}</span>
          {w.is_sponsored && (
            <span
              className="font-mono text-[10px] px-1.5 py-0.5 border"
              style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
            >
              <Gift size={10} className="inline mr-1" />PERK INCLUDED
            </span>
          )}
        </div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">{w.title}</h1>
        <div className="flex items-center gap-2 text-[#F5F2E8]/70">
          <MapPin size={14} /> {w.city}
        </div>
        {w.photo_url && (
          <img
            src={w.photo_url}
            alt={w.title}
            className="mt-4 w-full max-h-80 object-cover border border-[#F5F2E8]/10"
          />
        )}
      </div>

      {/* Locked / Unlocked story */}
      {!unlocked ? (
        <div className="loot-card-flat p-6 space-y-4" data-testid="waymark-locked">
          <div className="flex items-center gap-2 text-[#F5F2E8]/70">
            <Lock size={16} /> <span className="overline">Fun fact locked</span>
          </div>
          <p className="text-[#F5F2E8]/80 text-sm leading-relaxed">
            Walk up to this spot (within about 350 feet) to unlock the story behind it. We verify
            your location once — we never track you between visits.
          </p>
          <button
            onClick={claim}
            disabled={claiming}
            className="btn-loot inline-flex items-center gap-2"
            data-testid="waymark-unlock-btn"
          >
            {claiming ? <Loader2 size={14} className="animate-spin" /> : <Compass size={14} />}
            {claiming ? "Checking your location…" : "I'm here — unlock the story"}
          </button>
          {!user && (
            <div className="font-mono text-xs text-[#D7A62A]">
              You'll need a hunter account first — takes 15 seconds.
            </div>
          )}
        </div>
      ) : (
        <div className="loot-card-flat p-6 space-y-3" data-testid="waymark-unlocked">
          <div className="flex items-center gap-2 text-[#76B82A]">
            <Unlock size={16} /> <span className="overline">Fun fact unlocked</span>
          </div>
          <div className="text-[#F5F2E8]/90 text-base leading-relaxed whitespace-pre-line">
            {w.fun_fact}
          </div>
          {w.is_sponsored && (w.coupon_photo_url || w.coupon_terms || w.coupon_code) && (
            <div
              className="mt-5 p-5 border"
              style={{ borderColor: "#D7A62A", background: "rgba(215,166,42,0.08)" }}
              data-testid="waymark-coupon"
            >
              <div className="flex items-center gap-2 text-[#D7A62A]">
                <Gift size={16} /> <span className="overline">Your perk</span>
              </div>
              {w.sponsor?.business_name && (
                <div className="font-display uppercase text-2xl mt-2" style={{ color: "#D7A62A" }}>
                  {w.sponsor.business_name}
                </div>
              )}
              {w.coupon_terms && (
                <p className="text-[#F5F2E8]/90 text-sm mt-2 leading-relaxed">{w.coupon_terms}</p>
              )}
              {w.coupon_photo_url && (
                <img
                  src={w.coupon_photo_url}
                  alt="Coupon"
                  className="mt-3 w-full max-w-md border border-[#D7A62A]/40"
                />
              )}
              {w.coupon_code && (
                <div className="mt-3 font-mono text-base px-3 py-2 inline-block bg-[#1a1a1a] border border-[#D7A62A]">
                  Code: <span style={{ color: "#D7A62A" }}>{w.coupon_code}</span>
                </div>
              )}
              <p className="mt-3 font-mono text-[10px] text-[#F5F2E8]/50 uppercase tracking-widest">
                Show this screen at the counter to redeem.
              </p>
            </div>
          )}
          <div className="flex items-center gap-2 mt-4 text-sm text-[#76B82A]">
            <CheckCircle2 size={14} /> Stamped in your Loot Passport.
          </div>
        </div>
      )}
    </div>
  );
}
